/** Dispatched after GL-affecting API mutations so dashboard data can soft-refresh. */
export const DASHBOARD_DATA_SOFT_REFRESH =
  'abrasoft:dashboard-data-soft-refresh';

const staleCompanyIds = new Set<string>();

function staleKey(companyId?: string | null): string {
  return companyId?.trim() ? companyId.trim() : '__global__';
}

/** Marks dashboard metrics stale for the company; listeners may refresh in background. */
export function markDashboardDataStale(companyId?: string | null): void {
  staleCompanyIds.add(staleKey(companyId));
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(DASHBOARD_DATA_SOFT_REFRESH));
}

export function isDashboardDataStale(companyId?: string | null): boolean {
  return (
    staleCompanyIds.has(staleKey(companyId)) ||
    staleCompanyIds.has('__global__')
  );
}

export function clearDashboardDataStale(companyId?: string | null): void {
  staleCompanyIds.delete(staleKey(companyId));
  staleCompanyIds.delete('__global__');
}

export function invalidateDashboardDataCache(companyId?: string | null): void {
  markDashboardDataStale(companyId);
}

/** True when the response likely changes dashboard metrics (Abrafi scope). */
export function shouldInvalidateDashboardAfterMutation(
  method: string,
  endpoint: string,
): boolean {
  const m = method.toUpperCase();
  if (m !== 'POST' && m !== 'PATCH' && m !== 'PUT' && m !== 'DELETE') {
    return false;
  }
  const e = endpoint.replace(/^\/+/, '').split('?')[0].toLowerCase();
  if (e.startsWith('transactions')) {
    if (e.includes('/export')) return false;
    if (e.includes('receipt-upload-usage')) return false;
    if (e.includes('/receipt-image')) return false;
    if (e.includes('/receipt-images')) return false;
    return true;
  }
  if (e.startsWith('cash-receipts-journal')) {
    if (e.includes('/export')) return false;
    if (e.includes('/import/csv/validate')) return false;
    return true;
  }
  if (e.startsWith('cash-disbursements-journal')) {
    if (e.includes('/export')) return false;
    if (e.includes('/import/csv/validate')) return false;
    return true;
  }
  if (/^ap-bills\/[^/]+\/cancel$/.test(e)) {
    return true;
  }
  if (e.startsWith('capitals')) {
    return true;
  }
  return false;
}

export function notifyDashboardCacheIfNeeded(
  method: string,
  endpoint: string,
  companyId?: string | null,
): void {
  if (shouldInvalidateDashboardAfterMutation(method, endpoint)) {
    invalidateDashboardDataCache(companyId);
  }
}
